import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Certificate, ArrowClockwise, Play, XCircle, Target } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { LESSON_QUIZZES, getLessonQuiz } from "@/data/quizzes";
import LessonQuiz from "@/components/quiz/LessonQuiz";
import CertificatePanel from "@/components/exam/CertificatePanel";
import WeakSpots from "@/components/exam/WeakSpots";
import PracticeRound from "@/components/exam/PracticeRound";

const EXAM_TOTAL = 30;
const PASS = Math.ceil(EXAM_TOTAL * 0.8);
const POOL = Object.entries(LESSON_QUIZZES).flatMap(([path, q]) => q.en.map((_, i) => ({ path, i })));

const pick = () => {
  const a = [...POOL];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, EXAM_TOTAL);
};

export default function ExamPage() {
  const { lang } = useTranslation();
  const es = lang === "es";
  const [picks, setPicks] = useState(null);
  const [round, setRound] = useState(0);
  const [result, setResult] = useState(null);
  const [cert, setCert] = useState(null);
  const [last, setLast] = useState(null);
  const [practicing, setPracticing] = useState(false);

  useEffect(() => {
    axios
      .get("/api/exam/me", { withCredentials: true })
      .then((r) => {
        setCert(r.data.certificate);
        setLast(r.data.last_attempt);
      })
      .catch(() => {});
  }, []);

  const questions = useMemo(() => (picks ? picks.map((p) => getLessonQuiz(p.path, lang)[p.i]) : []), [picks, lang]);
  const missedCount = last?.missed?.length || 0;

  const startPractice = () => {
    setPicks(null);
    setResult(null);
    setPracticing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const begin = () => {
    setPracticing(false);
    setPicks(pick());
    setRound((r) => r + 1);
    setResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onFinish = async (score, total, missedIdx = []) => {
    const missed = missedIdx.map((i) => picks[i]);
    try {
      const { data } = await axios.post("/api/exam/result", { score, total, missed }, { withCredentials: true });
      setResult(data);
      setLast({ score: data.score, total: data.total, missed: data.missed || [] });
      if (data.passed) {
        setCert(data.certificate);
        toast.success(es ? "¡Aprobaste! Tu certificado está listo." : "You passed! Your certificate is ready.");
      }
      setTimeout(() => document.querySelector("[data-testid=exam-result]")?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch {
      toast.error(es ? "No se pudo guardar tu resultado" : "Couldn't save your result");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="exam-page">
      <div>
        <p className="flex items-center gap-1.5 text-[#F5A524] text-xs uppercase tracking-[0.2em] mb-2">
          <Certificate weight="fill" className="w-4 h-4" />
          {es ? "Aprender" : "Learn"}
        </p>
        <h1 className="font-['Outfit'] text-4xl sm:text-5xl text-white font-semibold tracking-tight mb-2">
          {es ? "Examen Final del Curso" : "Final Course Exam"}
        </h1>
        <p className="text-[#71717A] text-base">
          {es
            ? `${EXAM_TOTAL} preguntas al azar de las ${Object.keys(LESSON_QUIZZES).length} lecciones. Aprueba con ${PASS}/${EXAM_TOTAL} (80%) y obtén tu certificado para descargar y compartir.`
            : `${EXAM_TOTAL} random questions from all ${Object.keys(LESSON_QUIZZES).length} lessons. Pass with ${PASS}/${EXAM_TOTAL} (80%) to earn a certificate you can download and share.`}
        </p>
      </div>

      {practicing ? (
        <PracticeRound refs={last.missed} onExit={() => setPracticing(false)} onStartExam={begin} />
      ) : (
      <>
      {!picks && (
        <div className="flex flex-wrap items-center gap-2">
        <Button onClick={begin} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold" data-testid="exam-start-btn">
          <Play weight="fill" className="w-4 h-4 mr-1.5" />
          {cert ? (es ? "Volver a presentar el examen" : "Retake the exam") : es ? "Comenzar examen" : "Start the exam"}
        </Button>
        {missedCount > 0 && <PracticeButton es={es} n={missedCount} onClick={startPractice} testId="exam-practice-btn" />}
        </div>
      )}

      {picks && (
        <LessonQuiz key={`${round}-${lang}`} questions={questions} testPrefix="exam" onFinish={onFinish} allowRetry={false} />
      )}

      {result && !result.passed && (
        <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-5 flex items-center gap-4 flex-wrap" data-testid="exam-result">
          <XCircle weight="fill" className="w-8 h-8 text-red-400" />
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold">
              {es ? `Obtuviste ${result.score}/${result.total}` : `You scored ${result.score}/${result.total}`}
            </p>
            <p className="text-[#A1A1AA] text-sm">
              {es ? `Necesitas ${PASS}/${EXAM_TOTAL} para aprobar. Repasa las lecciones e inténtalo de nuevo.` : `You need ${PASS}/${EXAM_TOTAL} to pass. Review the lessons and try again.`}
            </p>
          </div>
          <Button onClick={begin} variant="outline" className="border-white/15 text-[#D4D4D8] hover:bg-white/5" data-testid="exam-new-btn">
            <ArrowClockwise weight="bold" className="w-4 h-4 mr-1.5" />
            {es ? "Nuevo examen" : "New exam"}
          </Button>
        </div>
      )}

      {result?.passed && (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="exam-result">
          <p className="text-emerald-300 font-semibold">
            {es ? `¡Aprobado! ${result.score}/${result.total}` : `Passed! ${result.score}/${result.total}`}
          </p>
          <Button onClick={begin} variant="outline" size="sm" className="border-white/15 text-[#D4D4D8] hover:bg-white/5" data-testid="exam-new-btn">
            <ArrowClockwise weight="bold" className="w-4 h-4 mr-1.5" />
            {es ? "Nuevo examen" : "New exam"}
          </Button>
        </div>
      )}

      {last?.missed && (!picks || result) && (
        <>
          {result && missedCount > 0 && <PracticeButton es={es} n={missedCount} onClick={startPractice} testId="exam-result-practice-btn" />}
          <WeakSpots missed={last.missed} score={last.score} total={last.total} />
        </>
      )}

      {cert && (
        <section className="space-y-3" data-testid="exam-certificate-section">
          <h2 className="font-['Outfit'] text-white text-xl font-semibold">{es ? "Tu certificado" : "Your certificate"}</h2>
          <CertificatePanel cert={cert} />
        </section>
      )}
      </>
      )}
    </div>
  );
}

function PracticeButton({ es, n, onClick, testId }) {
  return (
    <Button onClick={onClick} variant="outline" className="border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524]/10" data-testid={testId}>
      <Target weight="fill" className="w-4 h-4 mr-1.5" />
      {es ? `Practicar solo mis falladas (${n})` : `Practice only my missed questions (${n})`}
    </Button>
  );
}
